import { Link } from "react-router-dom";
import Seo from "../lib/Seo";
import { CONTACT_EMAIL } from "../lib/api";

const PLACEHOLDER = (
  <p className="placeholder">
    [Company registration details to be added once the business is registered. This policy operates in the name of Dialed In (getdialedin.golf) until then.]
  </p>
);

const LegalShell = ({ title, description, children, testid }) => (
  <>
    <Seo title={`${title} | Dialed In`} description={description} />
    <section className="page-head" data-testid={testid}>
      <div className="wrap">
        <div className="label">Legal</div>
        <h1 className="disp page-h" style={{ fontSize: "clamp(32px, 5.5vw, 68px)" }}>{title}</h1>
      </div>
    </section>
    <section className="sec sec-2">
      <div className="wrap"><div className="prose">{children}</div></div>
    </section>
  </>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" description="How Dialed In collects, uses and protects your data." testid="privacy-page">
    <p>Last updated: February 2026</p>
    {PLACEHOLDER}

    <h2>What we collect</h2>
    <ul>
      <li><strong style={{ color: "var(--cream)" }}>Email signups</strong> — your first name and email address when you join Dialed In, join the Prepare waitlist or sign up via the Golfer Profile quiz.</li>
      <li><strong style={{ color: "var(--cream)" }}>Quiz results</strong> — your golfer-type result, stored alongside your signup so we can send you a relevant breakdown.</li>
      <li><strong style={{ color: "var(--cream)" }}>Contact enquiries</strong> — your name, email and message when you use the contact form.</li>
      <li><strong style={{ color: "var(--cream)" }}>Research responses</strong> — collected through Jotform under Jotform's own privacy terms; responses may be quoted anonymously, never with your name.</li>
    </ul>

    <h2>How we use it</h2>
    <p>To send you what you asked for: The Science &middot; Weekly, product-development updates, Prepare waitlist updates and quiz-result breakdowns. To reply to your enquiries. To understand what golfers need so we can build better products.</p>
    <p>We do not sell your data. We do not share it with third parties for their marketing.</p>

    <h2>Where it lives</h2>
    <p>Email marketing runs on Mailchimp (Intuit Inc.), which stores your name, email, tags and signup source under its own privacy policy. Contact enquiries and quiz results are stored in our own database.</p>

    <h2>Cookies and analytics</h2>
    <p>If analytics are enabled, they only run after you accept cookies via the consent banner. See our <Link to="/cookies">Cookie Policy</Link>.</p>

    <h2>Your rights</h2>
    <p>You can unsubscribe from any email at any time using the link in the footer of every email. You can request access to, correction of, or deletion of your data by emailing <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We'll act on requests promptly.</p>

    <h2>Contact</h2>
    <p>Questions about this policy: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms & Conditions" description="Terms and conditions for using getdialedin.golf." testid="terms-page">
    <p>Last updated: February 2026</p>
    {PLACEHOLDER}

    <h2>About this site</h2>
    <p>getdialedin.golf is the home of Dialed In, a golf wellbeing brand in development. The site exists to share the build, invite research participation and collect email signups from people who want to follow along.</p>

    <h2>No products are sold yet</h2>
    <p>Prepare, Reset, Sustain and Recover are in development. Nothing on this site is an offer for sale. Product descriptions reflect development intent and may change before launch.</p>

    <h2>Not medical advice</h2>
    <p>Content on this site and in The Science &middot; Weekly is shared for general interest. It is not medical advice, diagnosis or treatment, and it makes no medical claims. If you have health concerns, speak to a qualified professional.</p>

    <h2>Your signups</h2>
    <p>By joining Dialed In or a waitlist you consent to receive the emails described at the point of signup. You can unsubscribe at any time.</p>

    <h2>Intellectual property</h2>
    <p>The Dialed In name, logo, copy and imagery on this site belong to Dialed In and may not be reproduced without permission.</p>

    <h2>Liability</h2>
    <p>This site is provided as-is. To the maximum extent permitted by law, Dialed In accepts no liability for loss arising from use of the site or reliance on its content.</p>

    <h2>Contact</h2>
    <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
  </LegalShell>
);

export const Cookies = () => (
  <LegalShell title="Cookie Policy" description="How getdialedin.golf uses cookies." testid="cookies-page">
    <p>Last updated: February 2026</p>

    <h2>The short version</h2>
    <p>No analytics or tracking cookies run on this site unless you accept them via the consent banner.</p>

    <h2>Strictly necessary storage</h2>
    <p>We use your browser's local storage to remember your cookie choice. That's it — it identifies nothing about you.</p>

    <h2>Analytics cookies (only with consent)</h2>
    <p>When enabled and accepted, the following may set cookies to help us understand how golfers find and use the site:</p>
    <ul>
      <li>Google Analytics 4 — traffic and usage patterns</li>
      <li>Meta Pixel — how visitors from Instagram/Facebook use the site</li>
      <li>TikTok Pixel — how visitors from TikTok use the site</li>
    </ul>
    <p>You can decline these entirely, and the site works exactly the same.</p>

    <h2>Changing your mind</h2>
    <p>Clear your browser's site data for getdialedin.golf and the consent banner will ask again on your next visit.</p>

    <h2>Contact</h2>
    <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
  </LegalShell>
);
